import { useState } from "react";
import { motion } from "framer-motion";
import { Megaphone, Trash, PaperPlaneTilt } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { ImageUploader } from "./ImageUploader";
import api, { mediaUrl } from "../lib/api";
import { timeAgo } from "../lib/helpers";
import { toast } from "sonner";

export const AnnouncementCard = ({ ann, isAdmin, onDeleted }) => {
  const del = async () => {
    try {
      await api.delete(`/announcements/${ann.id}`);
      toast.success("Announcement removed");
      onDeleted(ann.id);
    } catch {
      toast.error("Could not remove announcement");
    }
  };
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="bg-[#2C362B] text-[#F4F1E8] rounded-[2rem] p-6 sm:p-7 mb-4"
      data-testid="announcement-card"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Megaphone size={20} weight="fill" className="text-[#F8CDB0]" />
          <span className="text-xs font-bold tracking-[0.15em] uppercase text-[#B3C0AE]">Announcement</span>
        </div>
        {isAdmin && (
          <button onClick={del} className="text-[#8A9A86] hover:text-[#F8CDB0] transition-colors" data-testid="delete-announcement-btn" title="Remove">
            <Trash size={18} weight="duotone" />
          </button>
        )}
      </div>
      <p className="text-lg leading-relaxed whitespace-pre-wrap break-words" data-testid="announcement-message">{ann.message}</p>
      {ann.images && ann.images.length > 0 && (
        <div className={`grid gap-2 mt-4 ${ann.images.length === 1 ? "grid-cols-1" : "grid-cols-2"}`}>
          {ann.images.map((img, i) => (
            <img key={i} src={mediaUrl(img)} alt="announcement" className="w-full rounded-2xl object-cover max-h-96" />
          ))}
        </div>
      )}
      <p className="text-xs text-[#8A9A86] mt-4">{timeAgo(ann.created_at)}</p>
    </motion.div>
  );
};

export const AnnouncementComposer = ({ onCreated }) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [paths, setPaths] = useState([]);
  const [posting, setPosting] = useState(false);

  if (!user || user.role !== "admin") return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    setPosting(true);
    try {
      const res = await api.post("/announcements", { message, image_paths: paths });
      onCreated(res.data);
      setMessage("");
      setPaths([]);
      setOpen(false);
      toast.success("Announcement posted 📣");
    } catch {
      toast.error("Could not post announcement");
    } finally {
      setPosting(false);
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="w-full flex items-center justify-center gap-2 bg-[#2C362B] text-[#F4F1E8] rounded-[2rem] px-6 py-4 font-semibold mb-4 hover:bg-[#3a4638] transition-colors"
        data-testid="new-announcement-btn"
      >
        <Megaphone size={18} weight="fill" className="text-[#F8CDB0]" /> Post an announcement
      </button>
    );
  }

  return (
    <form onSubmit={submit} className="bg-[#2C362B] rounded-[2rem] p-6 mb-4" data-testid="announcement-composer">
      <textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder="Share an announcement with everyone…"
        maxLength={2000}
        className="w-full bg-[#3a4638] text-[#F4F1E8] border-none rounded-2xl p-4 placeholder:text-[#8A9A86] focus:ring-2 focus:ring-[#8A9A86] resize-none outline-none min-h-[90px] mb-3"
        data-testid="announcement-input"
      />
      <div className="mb-3">
        <ImageUploader paths={paths} onChange={setPaths} testid="announcement-image-uploader" />
      </div>
      <div className="flex items-center justify-end gap-2">
        <button type="button" onClick={() => setOpen(false)} className="text-[#B3C0AE] hover:text-[#F4F1E8] rounded-full px-4 py-2 text-sm font-semibold">Cancel</button>
        <button type="submit" disabled={posting || !message.trim()} className="flex items-center gap-2 bg-[#F8CDB0] text-[#2C362B] rounded-full px-5 py-2.5 text-sm font-semibold hover:bg-[#f5bd97] transition-colors disabled:opacity-50" data-testid="announcement-submit-btn">
          <PaperPlaneTilt size={16} weight="fill" /> {posting ? "Posting…" : "Post"}
        </button>
      </div>
    </form>
  );
};
